import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = searchParams.get("token") || "";

  const [status, setStatus] = useState(token ? "verifying" : "missing"); // missing | verifying | done | error
  const [err, setErr] = useState("");
  const ranRef = useRef(false);

  useEffect(() => {
    if (!token || ranRef.current) return;
    ranRef.current = true; // guard against React StrictMode's double-invoke consuming the single-use token
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        await refresh();
        setStatus("done");
      } catch (er) {
        setErr(formatApiErrorDetail(er.response?.data?.detail) || er.message);
        setStatus("error");
      }
    })();
  }, [token, refresh]);

  if (status === "missing") {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <AlertCircle size={40} className="text-eduhu-error mx-auto mb-4" />
          <h1 className="font-serif text-3xl text-eduhu-dark">Invalid link</h1>
          <p className="mt-3 text-eduhu-inkSoft">
            This verification link is missing a token. Please register again.
          </p>
          <Link to="/register" className="mt-6 inline-block eduhu-btn-primary">
            Back to registration
          </Link>
        </div>
      </div>
    );
  }

  if (status === "verifying") {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <Loader2 size={40} className="text-eduhu-gold mx-auto mb-4 animate-spin" />
          <h1 className="font-serif text-3xl text-eduhu-dark">Verifying…</h1>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <AlertCircle size={40} className="text-eduhu-error mx-auto mb-4" />
          <h1 className="font-serif text-3xl text-eduhu-dark">Verification failed</h1>
          <p className="mt-3 text-eduhu-inkSoft">{err || "This link is invalid or has expired."}</p>
          <Link to="/register" className="mt-6 inline-block eduhu-btn-primary">
            Register again
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-8">
      <div className="text-center max-w-sm">
        <CheckCircle2 size={48} className="text-eduhu-success mx-auto mb-6" />
        <h1 className="font-serif text-4xl text-eduhu-dark">Account verified</h1>
        <p className="mt-4 text-eduhu-inkSoft">Your EDUHUB account is active. Welcome aboard.</p>
        <button onClick={() => navigate("/dashboard")} className="mt-8 eduhu-btn-primary">
          Go to dashboard
        </button>
      </div>
    </div>
  );
}
